/**
 * API client wrapper. All requests to the api service go through here so we
 * get consistent base URL, credentials handling, error shape, and typing.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const AUTH_TOKEN_KEY = 'ojb.authToken';

/**
 * Auth token storage.
 *
 * Security audit C2: previously the bearer (which IS a live
 * session credential) lived in `localStorage` for ~14 days. Any
 * future XSS — including a single bad dangerouslySetInnerHTML
 * (we currently have 2, both server-escaped) or a third-party
 * dependency CVE — could read the token and replay it for the
 * full 14-day session lifetime.
 *
 * Mitigation: moved the bearer to `sessionStorage`. The token is
 * tab-scoped and cleared on tab close, so an XSS-stolen credential
 * dies with the tab. Real users get the same UX (the OAuth flow
 * lands on the SPA, captures the fragment, stores the token; the
 * tab stays open through normal use); a leaked token from history
 * /referrer / extension exfiltration becomes useless once the
 * tab closes.
 *
 * Migration: legacy tokens that were stored in localStorage from
 * the prior code path are migrated into sessionStorage on first
 * load and the localStorage entry is cleared. Without this step,
 * everyone with an open tab would be silently signed out the
 * first time they load the new build.
 *
 * Longer-term (separate fix): kill the bearer flow entirely by
 * moving both apps under one parent domain and relying on the
 * cookie. The bearer exists only because the cross-subdomain
 * cookie can't always be sent.
 */
function migrateLegacyAuthToken(): void {
  try {
    const legacy = localStorage.getItem(AUTH_TOKEN_KEY);
    if (legacy && !sessionStorage.getItem(AUTH_TOKEN_KEY)) {
      sessionStorage.setItem(AUTH_TOKEN_KEY, legacy);
    }
    if (legacy) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch {
    // localStorage / sessionStorage may be blocked (private mode,
    // strict ITP). Silent fallback — user re-signs-in if needed.
  }
}
migrateLegacyAuthToken();

/** Auth token helpers — used by DevSignIn to persist, and by apiFetch to send. */
export const authToken = {
  get: () => {
    try {
      return sessionStorage.getItem(AUTH_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (t: string) => {
    try {
      sessionStorage.setItem(AUTH_TOKEN_KEY, t);
      // Defense-in-depth: ensure no stale localStorage entry can
      // override a tab-scoped token. Belt + braces.
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } catch {
      // Storage blocked — apiFetch will just not send Bearer.
      // The cookie path (when available) still authenticates.
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } catch {
      // ignore
    }
  },
};

/**
 * CSRF token cache + lazy fetcher.
 *
 * The api enforces double-submit-cookie CSRF protection on every
 * state-changing request. Server sets a signed cookie + returns
 * the matching plain token via GET /csrf-token; client must echo
 * the plain token back as `x-csrf-token` on every POST/PATCH/
 * DELETE/PUT.
 *
 * We cache the token in module-scope memory (NOT localStorage —
 * keeps it out of XSS reach) and fetch lazily on first use. The
 * token survives within the SPA session; rotation happens on
 * page reload (via the next /csrf-token call) or on explicit
 * invalidation after a 403 with code: 'CSRF_INVALID' (the
 * apiFetch retry path below).
 */
let csrfTokenCache: string | null = null;
let csrfFetchInFlight: Promise<string> | null = null;

async function getCsrfToken(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache;
  if (csrfFetchInFlight) return csrfFetchInFlight;
  // Coalesce concurrent callers onto a single fetch — common when
  // app-mount fires several mutations in parallel.
  csrfFetchInFlight = (async () => {
    try {
      const url = new URL('/csrf-token', API_BASE || window.location.origin);
      const headers: Record<string, string> = { Accept: 'application/json' };
      const tok = authToken.get();
      if (tok) headers['Authorization'] = `Bearer ${tok}`;
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`CSRF token fetch failed: HTTP ${res.status}`);
      }
      const json = (await res.json()) as { csrfToken?: string };
      if (!json.csrfToken) {
        throw new Error('CSRF token missing from /csrf-token response');
      }
      csrfTokenCache = json.csrfToken;
      return json.csrfToken;
    } finally {
      csrfFetchInFlight = null;
    }
  })();
  return csrfFetchInFlight;
}

/** Force a refetch on next state-changing call. Used after a 403
 *  with code='CSRF_INVALID' so the retry mints a fresh token. */
function invalidateCsrfToken(): void {
  csrfTokenCache = null;
}

const STATE_CHANGING = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export class ApiError extends Error {
  public status: number;
  public data: unknown;
  constructor(status: number, message: string, data: unknown) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

export async function apiFetch<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, query } = opts;

  const url = new URL(path, API_BASE || window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, String(v));
      }
    }
  }

  // Build the request headers. CSRF token attaches to state-
  // changing methods only; GETs are ignored by the server's CSRF
  // gate and don't need it.
  const buildHeaders = async (): Promise<Record<string, string>> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    // Cross-origin cookies are blocked by many browsers' default third-party
    // cookie policies. Send the session id as a Bearer token when present.
    const token = authToken.get();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (STATE_CHANGING.has(method)) {
      headers['X-CSRF-Token'] = await getCsrfToken();
    }
    return headers;
  };

  const doRequest = async () =>
    fetch(url.toString(), {
      method,
      headers: await buildHeaders(),
      credentials: 'include', // send session cookie cross-origin
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });

  let res = await doRequest();

  // CSRF token mismatch — invalidate the cached token, refetch a
  // fresh one, and retry the original request once. This handles
  // the case where the user's session rotated (sign-in/sign-out
  // in another tab, server bounced) but the client is still
  // sending a stale token. We retry only ONCE to avoid loops.
  if (res.status === 403 && STATE_CHANGING.has(method)) {
    let body403: { code?: string } | null = null;
    try {
      body403 = (await res.clone().json()) as { code?: string };
    } catch {
      /* not JSON — fall through to normal error path */
    }
    if (body403?.code === 'CSRF_INVALID') {
      invalidateCsrfToken();
      res = await doRequest();
    }
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      (isJson && data && typeof data === 'object' && 'error' in data && String(data.error)) ||
      `HTTP ${res.status}`;
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

/** Convenience wrappers */
export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    apiFetch<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

/**
 * Download a binary file from the API — used for .docx / .pdf exports.
 * Reuses auth + base URL, parses Content-Disposition for the filename,
 * and triggers a browser download. Rejects with ApiError on non-2xx.
 */
export async function downloadFile(
  path: string,
  query?: RequestOptions['query'],
  fallbackFilename = 'download',
): Promise<void> {
  const url = new URL(path, API_BASE || window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, String(v));
      }
    }
  }

  const headers: Record<string, string> = {};
  const token = authToken.get();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    // Try to surface a readable JSON error message
    let message = `HTTP ${res.status}`;
    let data: unknown = null;
    try {
      data = await res.json();
      if (data && typeof data === 'object' && 'error' in data) {
        message = String((data as { error: unknown }).error);
      }
    } catch {
      // Not JSON — leave default message
    }
    throw new ApiError(res.status, message, data);
  }

  const filename = parseFilenameFromHeader(res.headers.get('content-disposition')) ?? fallbackFilename;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Delay revoke slightly so Safari definitely had time to start the download
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

/**
 * POST a JSON body and download the binary response as a file. Used by
 * the Email flow (POST /meetings/:id/send-email returns a generated
 * .eml). Parses Content-Disposition for the filename and triggers a
 * browser download like `downloadFile` does, but also returns the
 * response's custom headers so callers can read side-channel data
 * (e.g. X-Meeting-Finalized) without a second request.
 */
export async function postAndDownload(
  path: string,
  body: unknown,
  fallbackFilename = 'download',
): Promise<{ filename: string; headers: Headers }> {
  const url = new URL(path, API_BASE || window.location.origin);
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = authToken.get();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // POST is state-changing, so the CSRF gate applies here too.
  headers['X-CSRF-Token'] = await getCsrfToken();

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let data: unknown = null;
    try {
      data = await res.json();
      if (data && typeof data === 'object' && 'error' in data) {
        message = String((data as { error: unknown }).error);
      }
    } catch {
      // Not JSON; leave default message
    }
    throw new ApiError(res.status, message, data);
  }

  const filename =
    parseFilenameFromHeader(res.headers.get('content-disposition')) ?? fallbackFilename;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  return { filename, headers: res.headers };
}

/**
 * POST a JSON body and consume a Server-Sent Events stream from the
 * response. Each line of shape `event: <name>\ndata: <json>\n\n` is parsed
 * into `{ event, data }` and handed to `onEvent`. Resolves when the stream
 * closes (typical SSE pattern) or rejects on non-2xx / network error.
 *
 * Used by the New Meeting flow to get per-phase transform progress events.
 */
export async function postAndStream(
  path: string,
  body: unknown,
  onEvent: (event: string, data: unknown) => void,
  signal?: AbortSignal,
): Promise<void> {
  const url = new URL(path, API_BASE || window.location.origin);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const token = authToken.get();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // POST + stream is state-changing → CSRF gate applies.
  headers['X-CSRF-Token'] = await getCsrfToken();

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let data: unknown = null;
    try {
      data = await res.json();
      if (data && typeof data === 'object' && 'error' in data) {
        message = String((data as { error: unknown }).error);
      }
    } catch {
      // not JSON — keep default
    }
    throw new ApiError(res.status, message, data);
  }
  if (!res.body) {
    throw new ApiError(500, 'Streaming response missing body', null);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE messages are separated by a blank line (\n\n). Split on that
    // and process any complete messages, keeping the remainder in the
    // buffer for the next chunk.
    let nl: number;
    while ((nl = buffer.indexOf('\n\n')) !== -1) {
      const rawMessage = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 2);
      let eventName = 'message';
      const dataLines: string[] = [];
      for (const line of rawMessage.split('\n')) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5).trim());
        }
      }
      if (dataLines.length === 0) continue;
      const dataStr = dataLines.join('\n');
      let parsed: unknown = dataStr;
      try {
        parsed = JSON.parse(dataStr);
      } catch {
        // keep as string if not JSON
      }
      onEvent(eventName, parsed);
    }
  }
}

function parseFilenameFromHeader(header: string | null): string | null {
  if (!header) return null;
  // Matches filename="foo.docx" or filename=foo.docx
  const match =
    header.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i) ||
    header.match(/filename=([^;]+)/i);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1].trim().replace(/^"|"$/g, ''));
  } catch {
    return match[1].trim().replace(/^"|"$/g, '');
  }
}
